import { FC } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import './Button.scss';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { getLocalizedLink } from '@/utils/getLocalizedLink';
import { useSettingsContext } from '@/contexts/SettingsContext';

interface ButtonProps {
    backgroundColor?: 'Navy' | 'Pink';
    label: string;
    path?: string;
    target?: string;
    id?: string;
    onClickCallback?: () => void;
    type?: 'submit' | 'button' | 'reset';
    disabled?: boolean;
    className?: string;
    onLinkClickCallback?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const Button: FC<ButtonProps> = ({
    backgroundColor = 'Pink',
    label,
    path,
    target = '_self',
    onClickCallback,
    id,
    type,
    disabled = false,
    className = '',
    onLinkClickCallback,
}) => {
    const buttonClassNames = classNames(
        'custom-btn',
        {
            'custom-btn--pink': backgroundColor === 'Pink',
            'custom-btn--navy': backgroundColor === 'Navy',
        },
        'custom-btn--text',
        className,
    );
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    const { lang } = useSettingsContext();

    let finalPath = path;

    if (path && lang && path.startsWith('/')) {
        finalPath = getLocalizedLink(path, lang);
    }

    if (target === '_modal') {
        return (
            <div
                {...inspectorProps({
                    fieldId: 'buttonLabel',
                })}
                onClick={onClickCallback}
                className={buttonClassNames}
                role="button"
                tabIndex={0}
            >
                {label}
            </div>
        );
    }
    return (
        <>
            {path ? (
                <Link
                    href={finalPath!}
                    target={target}
                    className="hover:tw-no-underline"
                    onClick={onLinkClickCallback}
                >
                    <span
                        {...inspectorProps({
                            fieldId: 'buttonLabel',
                        })}
                        className={buttonClassNames}
                    >
                        {label}
                    </span>
                </Link>
            ) : (
                <button
                    type={type!}
                    disabled={disabled}
                    {...inspectorProps({
                        fieldId: 'buttonLabel',
                    })}
                    className={buttonClassNames}
                >
                    {label}
                </button>
            )}
        </>
    );
};

export default Button;
